/**
 * QudSpriteLoader - Loads Caves of Qud sprites into a texture atlas
 *
 * Handles the Qud-specific sprite index format and atlas extraction,
 * decoupled from the generic WebGL rendering system.
 */

import type { SpriteIndex, CompactSpriteIndex } from '@/qud/sprites/types';
import type { TextureAtlasAllocator } from '@/graphics/TextureAtlasAllocator';
import { CELL_WIDTH, CELL_HEIGHT } from '@/qud/sprites/constants';

export class QudSpriteLoader {
  private spriteIndex: SpriteIndex | null = null;
  private atlasImages: Map<string, HTMLImageElement> = new Map();
  private extractCanvas: HTMLCanvasElement;
  private extractCtx: CanvasRenderingContext2D;
  private basePath: string;
  private ready = false;

  // Sprite name -> cell index mapping
  private spriteToCell: Map<string, number> = new Map();

  constructor(basePath: string = '/data/sprites') {
    this.basePath = basePath;

    // Create extraction canvas
    this.extractCanvas = document.createElement('canvas');
    this.extractCanvas.width = CELL_WIDTH;
    this.extractCanvas.height = CELL_HEIGHT;
    this.extractCtx = this.extractCanvas.getContext('2d', { willReadFrequently: true })!;
    this.extractCtx.imageSmoothingEnabled = false;
  }

  /**
   * Load the sprite index and atlas images.
   * Must be called before loading sprites.
   */
  async initialize(): Promise<void> {
    // Load index
    const response = await fetch(`${this.basePath}/index.json`);
    const compactIndex: CompactSpriteIndex = await response.json();
    this.spriteIndex = this.expandCompactIndex(compactIndex);

    console.log(
      `QudSpriteLoader: Loaded index with ${Object.keys(this.spriteIndex.sprites).length} sprites, ` +
        `${Object.keys(this.spriteIndex.atlases).length} atlases`
    );

    // Load all atlas images
    const atlasIds = Object.keys(this.spriteIndex.atlases);
    await Promise.all(
      atlasIds.map(async (id) => {
        const atlas = this.spriteIndex!.atlases[id];
        const img = await this.loadImage(`${this.basePath}/atlases/${atlas.name}.png`);
        this.atlasImages.set(id, img);
      })
    );

    console.log(`QudSpriteLoader: Loaded ${atlasIds.length} atlas images`);
    this.ready = true;
  }

  /**
   * Check if the loader is initialized.
   */
  isReady(): boolean {
    return this.ready;
  }

  /**
   * Get the cell index for a sprite, or undefined if not loaded.
   * This is the lookup function for the renderer.
   */
  getCell(spriteName: string): number | undefined {
    return this.spriteToCell.get(spriteName);
  }

  /**
   * Get sprite metadata by name.
   */
  getSpriteMetadata(spriteName: string) {
    return this.spriteIndex?.sprites[spriteName];
  }

  /**
   * Load a sprite into the allocator.
   * Returns true if the sprite was loaded, false if it couldn't be loaded.
   */
  loadSprite(spriteName: string, allocator: TextureAtlasAllocator): boolean {
    if (!this.spriteIndex) return false;

    // Already loaded?
    if (this.spriteToCell.has(spriteName)) return true;

    const sprite = this.spriteIndex.sprites[spriteName];
    if (!sprite) return false;

    // Only support 16x24 sprites for now
    if (sprite.width !== CELL_WIDTH || sprite.height !== CELL_HEIGHT) {
      return false;
    }

    const atlasImg = this.atlasImages.get(sprite.atlasId);
    if (!atlasImg) return false;

    // Extract sprite from source atlas
    // Sprite Y coordinates are bottom-up (OpenGL convention), convert to top-down for canvas.
    const srcY = atlasImg.height - sprite.y - CELL_HEIGHT;
    this.extractCtx.clearRect(0, 0, CELL_WIDTH, CELL_HEIGHT);
    this.extractCtx.drawImage(
      atlasImg,
      sprite.x,
      srcY,
      CELL_WIDTH,
      CELL_HEIGHT,
      0,
      0,
      CELL_WIDTH,
      CELL_HEIGHT
    );

    // Allocate cell and upload
    const cellIndex = allocator.allocateAndUpload(this.extractCanvas);
    this.spriteToCell.set(spriteName, cellIndex);
    return true;
  }

  /**
   * Load multiple sprites into the allocator.
   * @param spriteNames Array of sprite names to load
   * @param allocator The allocator to load into
   * @returns Number of sprites successfully loaded
   */
  loadSprites(spriteNames: string[], allocator: TextureAtlasAllocator): number {
    let loaded = 0;
    for (const name of spriteNames) {
      if (this.loadSprite(name, allocator)) {
        loaded++;
      }
    }
    return loaded;
  }

  /**
   * Load all sprites used by a world map.
   * @param worldMap The world map to scan for sprites
   * @param allocator The allocator to load into
   * @returns Number of unique sprites loaded
   */
  loadSpritesForWorldMap(
    worldMap: { tiles: Array<Array<{ sprite?: string } | null>> },
    allocator: TextureAtlasAllocator
  ): number {
    const spriteNames = new Set<string>();

    // Collect unique sprite names
    for (const row of worldMap.tiles) {
      for (const tile of row) {
        if (tile?.sprite) {
          spriteNames.add(tile.sprite);
        }
      }
    }

    // Load them
    return this.loadSprites([...spriteNames], allocator);
  }

  private expandCompactIndex(compact: CompactSpriteIndex): SpriteIndex {
    const atlases: Record<string, { id: string; name: string; width: number; height: number }> = {};
    const sprites: SpriteIndex['sprites'] = {};

    for (const [id, name, width, height] of compact.atlases) {
      atlases[id] = { id, name, width, height };
    }

    for (const [spriteName, atlasIndex, x, y, width, height, shaderMode] of compact.sprites) {
      const atlas = compact.atlases[atlasIndex];
      const atlasId = atlas[0];
      const atlasName = atlas[1];

      sprites[spriteName] = {
        name: spriteName,
        atlasId,
        atlasName,
        x,
        y,
        width,
        height,
        shaderMode,
      };
    }

    return { atlases, sprites };
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }
}
